import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./UserLogin.css";
import logo from "../components/assets/logo.png";

// Local Storage Helper - FIXED default to 'user'
const LocalStorageHelper = {
  saveLoginData: (userId, email, name, userType = 'user') => {
    localStorage.setItem('isLoggedIn', 'true');
    localStorage.setItem('userId', userId);
    localStorage.setItem('userEmail', email);
    localStorage.setItem('userName', name);
    localStorage.setItem('userType', userType);
  },

  isLoggedIn: () => {
    return localStorage.getItem('isLoggedIn') === 'true';
  },

  getLoginData: () => {
    return {
      userId: localStorage.getItem('userId'),
      email: localStorage.getItem('userEmail'),
      name: localStorage.getItem('userName'),
      userType: localStorage.getItem('userType'),
    };
  },

  clearLoginData: () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');
    localStorage.removeItem('userType');
  }
};

const UserLogin = () => {
  const [userData, setUserData] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  const navigate = useNavigate();
  const googleProvider = new GoogleAuthProvider();

  useEffect(() => {
    document.body.classList.add("login-active");

    // Check if user is already logged in
    const checkLoginStatus = async () => {
      if (LocalStorageHelper.isLoggedIn()) {
        const loginData = LocalStorageHelper.getLoginData();
        if (loginData.userId) {
          // Verify user still exists in Firebase
          try {
            const collectionName = loginData.userType === "photographer" ? 'photographers' : 'users';
            const userDoc = await getDoc(doc(db, collectionName, loginData.userId));
            if (userDoc.exists()) {
              if (loginData.userType === "photographer") {
                navigate("/Upload");
              } else {
                navigate("/LandingPage");
              }
              return;
            } else {
              LocalStorageHelper.clearLoginData();
            }
          } catch (e) {
            console.error('Error verifying user:', e);
            LocalStorageHelper.clearLoginData();
          }
        }
      }
      setIsCheckingAuth(false);
    };

    checkLoginStatus();

    const params = new URLSearchParams(window.location.search);
    if (params.get("verified") === "true") {
      toast.success("Email verified successfully, you can now log in!", {
        autoClose: 3000,
        position: "top-right",
      });
    }

    return () => {
      document.body.classList.remove("login-active");
    };
  }, [navigate]);

  const handleChange = (e) => {
    setUserData({ ...userData, [e.target.name]: e.target.value });
    if (error) setError("");
  };

  const validateForm = () => {
    if (!userData.email || !userData.password) {
      setError("Please fill in all fields");
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userData.email)) {
      setError("Please enter a valid email address");
      return false;
    }
    if (userData.password.length < 6) {
      setError("Password must be at least 6 characters long");
      return false;
    }
    return true;
  };

  const handleLogin = async () => {
    if (!validateForm()) return;
    setIsLoading(true);
    setError("");

    try {
      // Sign in with Firebase - Check USERS collection FIRST for UserLogin
      const userCredential = await signInWithEmailAndPassword(auth, userData.email, userData.password);
      const user = userCredential.user;

      // ✅ FIXED: Check users collection FIRST
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userDocData = userDoc.data();
        
        // ✅ SAVE AS USER
        LocalStorageHelper.saveLoginData(
          user.uid,
          userDocData.email || userData.email,
          userDocData.name || 'User',
          'user'
        );

        toast.success("Login successful!", { autoClose: 2000 });
        navigate("/LandingPage");
        return;
      }

      // Check photographers collection second
      const photographerDocRef = doc(db, 'photographers', user.uid);
      const photographerDoc = await getDoc(photographerDocRef);

      if (photographerDoc.exists()) {
        const photographerData = photographerDoc.data();
        
        // ✅ SAVE AS PHOTOGRAPHER
        LocalStorageHelper.saveLoginData(
          user.uid,
          photographerData.email || userData.email,
          photographerData.name || 'Photographer',
          'photographer'
        );

        toast.success("Login successful!", { autoClose: 2000 });
        navigate("/Upload");
        return;
      }

      setError("No account found. Please register first.");
    } catch (err) {
      console.error('Login error:', err);
      setError(getFirebaseErrorMessage(err.code));
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ FIXED: Google login creates USER account (not photographer)
  const handleGoogleLogin = async () => {
    setIsLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // ✅ CHECK/CREATE USER ACCOUNT (not photographer)
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        // ✅ CREATE NEW USER ACCOUNT
        await setDoc(userDocRef, {
          name: user.displayName || 'Google User',
          email: user.email,
          profileImage: user.photoURL || '',
          phone: '',
          location: '',
          emailVerified: true,
          signInMethod: 'google',
          createdAt: new Date()
        });
      }

      // ✅ SAVE AS USER
      LocalStorageHelper.saveLoginData(
        user.uid,
        user.email,
        user.displayName || 'Google User',
        'user'
      );

      toast.success("✅ Google login successful! Welcome User!", { autoClose: 2000 });
      navigate("/LandingPage");
    } catch (err) {
      console.error('Google login error:', err);
      setError("Google sign-in failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFacebookLogin = async () => {
    setIsLoading(true);
    toast.info("🔄 Facebook login coming soon - using Google for now!", { autoClose: 2000 });
    
    // For now, redirect to Google login (USER behavior)
    setTimeout(() => {
      handleGoogleLogin();
    }, 1500);
  };

  const handleForgotPassword = async () => {
    if (!userData.email) {
      setError("Please enter your email address first");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, userData.email);
      toast.success("Password reset email sent! Check your inbox.", { autoClose: 5000 });
    } catch (err) {
      console.error('Password reset error:', err);
      toast.error("Error sending password reset email. Please try again.");
    }
  };

  const handleCreateUserAccount = () => {
    navigate("/CreateAccount");
  };

  // ✅ NEW: Link to Photographer login
  const handlePhotographerLogin = () => {
    navigate("/Login"); // Main photographer login page
  };

  const getFirebaseErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/user-not-found':
        return 'No account found with this email';
      case 'auth/wrong-password':
        return 'Incorrect password';
      case 'auth/invalid-email':
        return 'Invalid email address';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please try again later';
      case 'auth/user-disabled':
        return 'This account has been disabled';
      case 'auth/invalid-credential':
        return 'Invalid email or password';
      default:
        return 'Login failed. Please try again';
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Show loading screen while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="userlogin-page-body">
        <div className="login-container">
          <div className="login-loading-screen">
            <img src={logo} alt="LitStudio" className="login-logo" />
            <div className="login-spinner"></div>
            <p>Checking login status...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="userlogin-page-body">
      <div className="login-container">
        {/* Editorial panel */}
        <div className="login-left">
          <img src="/images/book-photoshoot.jpg" alt="" />
          {/* <img src={logo} alt="LitStudio" className="login-logo" /> */}

          <button
            type="button"
            className="login-back-button"
            onClick={() => navigate("/LandingPage")}
          >
            <svg className="login-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
            </svg>
            Back to website
          </button>

          <div>
            <div className="login-caption">Find the photographer who gets your vision.</div>
            <p className="login-caption-sub">
              Compare verified portfolios and pricing upfront, then book the
              photographer whose style fits your story.
            </p>
          </div>
        </div>

        {/* Form panel */}
        <div className="login-right">
          <div className="login-panel">
            <div className="login-role-toggle" role="tablist" aria-label="Choose account type">
              <button
                type="button"
                role="tab"
                aria-selected="false"
                className="login-role-btn"
                onClick={() => navigate("/Login")}
              >
                Photographer
              </button>
              <button type="button" role="tab" aria-selected="true" className="login-role-btn is-active">
                User
              </button>
            </div>

            <span className="login-badge">
              <svg className="login-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              User
            </span>

            <h2>Welcome back</h2>
            <p className="login-subtitle">
              Don&apos;t have an account?{" "}
              <button type="button" className="login-link-inline" onClick={() => navigate("/CreateAccount")}>
                Create one
              </button>
            </p>

            {error && (
              <div className="login-error" role="alert">
                <svg className="login-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            <form className="login-form" onSubmit={(e) => e.preventDefault()}>
              <div className="login-form-group">
                <label htmlFor="email">Email address</label>
                <input
                  id="email"
                  className="login-input"
                  name="email"
                  placeholder="you@example.com"
                  type="email"
                  autoComplete="email"
                  value={userData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="login-form-group">
                <label htmlFor="password">Password</label>
                <div className="login-password-wrap">
                  <input
                    id="password"
                    className="login-input"
                    name="password"
                    placeholder="Enter your password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={userData.password}
                    onChange={handleChange}
                    required
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={togglePasswordVisibility}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg className="login-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M17.94 17.94A10.1 10.1 0 0 1 12 20C5 20 1 12 1 12a18.5 18.5 0 0 1 5.06-5.94M9.9 4.24A9.1 9.1 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19M1 1l22 22" />
                      </svg>
                    ) : (
                      <svg className="login-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" /><circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="login-form-meta">
                <button type="button" className="login-text-btn is-accent" onClick={handleForgotPassword}>
                  Forgot password?
                </button>
                <button type="button" className="login-text-btn" onClick={handlePhotographerLogin}>
                  Are you a photographer?
                </button>
              </div>

              <button
                className="login-submit-btn"
                type="button"
                onClick={handleLogin}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <svg className="login-icon login-icon-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                      <path d="M21 12a9 9 0 1 1-6.2-8.6" />
                    </svg>
                    Signing in...
                  </>
                ) : (
                  "Log in"
                )}
              </button>
            </form>

            <div className="login-divider">or</div>

            <div className="login-social-login">
              <button className="login-social-btn" onClick={handleGoogleLogin} disabled={isLoading}>
                <svg viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.2 17.6 9.5 24 9.5Z" />
                  <path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.4c-.5 2.9-2.2 5.3-4.7 7l7.6 5.9c4.4-4.1 6.8-10.8 6.8-17.4Z" />
                  <path fill="#FBBC05" d="M10.4 28.7a14.6 14.6 0 0 1 0-9.4l-7.8-6.1a24 24 0 0 0 0 21.6l7.8-6.1Z" />
                  <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.6-5.9c-2.1 1.4-4.8 2.3-8.3 2.3-6.4 0-11.7-3.7-13.6-8.9l-7.8 6.1C6.5 42.6 14.6 48 24 48Z" />
                </svg>
                Continue with Google
              </button>
            </div>

            <p className="login-terms">
              By logging in you agree to our{" "}
              <button type="button" onClick={() => navigate("/AboutUs")}>Privacy Policy</button>{" "}
              and{" "}
              <button type="button" onClick={() => navigate("/AboutUs")}>Terms</button>.
            </p>
          </div>
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar newestOnTop closeOnClick pauseOnHover theme="light" />
    </div>
  );
};

export default UserLogin;
